/**
 * Shared Calculator Keypad
 *
 * Canonical presentation for the 4-function calculator (display + button
 * grid), built on the shared useCalculator hook. Used in BOTH Script
 * Runner (Quick Reference rail calculator, Calculator dialog context) and
 * Agent Workspace (Calculator toolbar tool) — this component has no
 * knowledge of either feature.
 *
 * This is the KEYPAD only (behavior + display + buttons), not an outer
 * card/dialog container — each consumer supplies its own surrounding
 * container (Card, Dialog, toolbar panel, etc.) and testid for that
 * container, matching where/how the calculator is already placed there.
 *
 * The main display always renders the hook's `expression` value (the full
 * in-progress calculation, e.g. "10 × 10"), never the raw single-operand
 * `display` — this is a general improvement for every consumer, not an
 * Agent-Workspace-only behavior: `expression` already collapses to
 * `display` whenever there is no pending operator context (before any
 * operator is pressed, and immediately after "="), so this is a strict
 * upgrade with no visible change to Script Runner's existing before/after
 * states, only to what's shown while composing a multi-key calculation.
 *
 * Calculation History is opt-in via `showHistory`, since only Agent
 * Workspace has the unused vertical space to justify it — Script Runner's
 * existing calculator surfaces are unaffected unless they explicitly pass
 * `showHistory`.
 *
 * Props:
 *   density          — "comfortable" (default, Script Runner sizing) or
 *                       "compact" (denser, for narrow containers such as
 *                       the ~288px Agent Workspace toolbar panel)
 *   className        — extra classes for the root wrapper
 *   testId           — root wrapper data-testid (optional)
 *   displayTestId    — data-testid for the display readout
 *   gridTestId       — data-testid for the button grid
 *   buttonTestIdPrefix — prefix for each button's data-testid, e.g.
 *                       "calc" -> "calc-clear", "calc-7", "calc-op-div"
 *   showHistory      — when true, renders a compact Calculation History
 *                       list below the keypad (default false)
 *   historyTestId    — data-testid for the History section (when shown)
 */
import React from "react";

import { Button } from "../ui/button";
import { useCalculator } from "../../hooks/useCalculator";

export function CalculatorKeypad({
  density = "comfortable",
  className = "",
  testId,
  displayTestId = "calculator-display",
  gridTestId = "calculator-grid",
  buttonTestIdPrefix = "calc",
  showHistory = false,
  historyTestId = "calculator-history",
}) {
  const {
    expression,
    history,
    clear,
    backspace,
    inputDigit,
    inputDot,
    setOperator,
    equals,
    handleKeyDown,
  } = useCalculator();

  const isCompact = density === "compact";
  const buttonSize = isCompact ? "sm" : "default";
  const buttonClass = "bg-white/10 text-slate-100 ring-1 ring-white/10 hover:bg-white/15";
  const gridGapClass = isCompact ? "gap-1.5" : "gap-2";
  const displayClass = isCompact
    ? "rounded-xl border border-white/10 bg-black/30 p-3 text-right font-mono text-xl text-slate-100 break-words"
    : "rounded-2xl border border-white/10 bg-black/30 p-4 text-right font-mono text-2xl text-slate-100 break-words";

  return (
    <div
      className={`space-y-3 ${className}`}
      data-testid={testId}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <div className={displayClass} data-testid={displayTestId}>
        {expression}
      </div>

      <div className={`grid grid-cols-4 ${gridGapClass}`} data-testid={gridTestId}>
        <Button
          variant="secondary"
          size={buttonSize}
          className={buttonClass}
          onClick={clear}
          data-testid={`${buttonTestIdPrefix}-clear`}
        >
          C
        </Button>
        <Button
          variant="secondary"
          size={buttonSize}
          className={buttonClass}
          onClick={backspace}
          data-testid={`${buttonTestIdPrefix}-backspace`}
        >
          ⌫
        </Button>
        <div />
        <Button
          variant="secondary"
          size={buttonSize}
          className={buttonClass}
          onClick={() => setOperator("/")}
          data-testid={`${buttonTestIdPrefix}-op-div`}
        >
          ÷
        </Button>

        {["7", "8", "9"].map((d) => (
          <Button
            key={d}
            variant="secondary"
            size={buttonSize}
            className={buttonClass}
            onClick={() => inputDigit(d)}
            data-testid={`${buttonTestIdPrefix}-${d}`}
          >
            {d}
          </Button>
        ))}
        <Button
          variant="secondary"
          size={buttonSize}
          className={buttonClass}
          onClick={() => setOperator("*")}
          data-testid={`${buttonTestIdPrefix}-op-mul`}
        >
          ×
        </Button>

        {["4", "5", "6"].map((d) => (
          <Button
            key={d}
            variant="secondary"
            size={buttonSize}
            className={buttonClass}
            onClick={() => inputDigit(d)}
            data-testid={`${buttonTestIdPrefix}-${d}`}
          >
            {d}
          </Button>
        ))}
        <Button
          variant="secondary"
          size={buttonSize}
          className={buttonClass}
          onClick={() => setOperator("-")}
          data-testid={`${buttonTestIdPrefix}-op-sub`}
        >
          −
        </Button>

        {["1", "2", "3"].map((d) => (
          <Button
            key={d}
            variant="secondary"
            size={buttonSize}
            className={buttonClass}
            onClick={() => inputDigit(d)}
            data-testid={`${buttonTestIdPrefix}-${d}`}
          >
            {d}
          </Button>
        ))}
        <Button
          variant="secondary"
          size={buttonSize}
          className={buttonClass}
          onClick={() => setOperator("+")}
          data-testid={`${buttonTestIdPrefix}-op-add`}
        >
          +
        </Button>

        <Button
          variant="secondary"
          size={buttonSize}
          className={`${buttonClass} col-span-2`}
          onClick={() => inputDigit("0")}
          data-testid={`${buttonTestIdPrefix}-0`}
        >
          0
        </Button>
        <Button
          variant="secondary"
          size={buttonSize}
          className={buttonClass}
          onClick={inputDot}
          data-testid={`${buttonTestIdPrefix}-dot`}
        >
          .
        </Button>
        <Button
          size={buttonSize}
          className="bg-indigo-500/90 text-white hover:bg-indigo-500"
          onClick={equals}
          data-testid={`${buttonTestIdPrefix}-equals`}
        >
          =
        </Button>
      </div>

      {showHistory ? (
        <div className="flex flex-col gap-1.5" data-testid={historyTestId}>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-300">
            History
          </p>
          {history.length === 0 ? (
            <p className="text-[11px] text-slate-600">No calculations yet.</p>
          ) : (
            <div className="flex flex-col gap-1">
              {history.map((entry) => (
                <div
                  key={entry.id}
                  className="truncate text-xs text-slate-400"
                  data-testid={`${historyTestId}-entry-${entry.id}`}
                >
                  {entry.expression} = {entry.result}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default CalculatorKeypad;
